import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/feature/PageSEO';
import { itemListToProductGraph } from '@/lib/seo';
import XRangeHero from './components/XRangeHero';
import SeriesOverview from './components/SeriesOverview';
import ProductGrid from './components/ProductGrid';
import TechnicalSpecifications from './components/TechnicalSpecifications';
import ApplicationShowcase from './components/ApplicationShowcase';
import DesignedForSmarterDeployment from './components/DesignedForSmarterDeployment';
import CallToAction from './components/CallToAction';
import Footer from '../home/components/Footer';

const xRangeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'X Series Luxury Living Pods',
  description: 'Nexa Pods X Series — high-end prefabricated space capsule living pods for luxury glamping and short-stay accommodation. Models X3, X5, X6, X7, Y6, V6 with premium construction and panoramic views.',
  numberOfItems: 6,
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@type': 'Product',
        name: 'X3 Space Capsule',
        description: 'Compact and agile, perfect for high-end glamping or private studios. Full X Series experience in a smaller package.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/x-range-details',
        image: 'https://nexapods.com.au/images/71de8de379a3.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '45760',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 2,
      item: {
        '@type': 'Product',
        name: 'X5 Space Capsule',
        description: 'Features a panoramic balcony and bedroom skylight for an immersive nature experience. Perfect for eco-tourism resorts.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/x-range-details',
        image: 'https://nexapods.com.au/images/65c0b0130fa8.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '54670',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 3,
      item: {
        '@type': 'Product',
        name: 'X6 Space Capsule',
        description: 'Extended living space with a dedicated equipment room for maximum utility. Perfect for off-grid installations.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/x-range-details',
        image: 'https://nexapods.com.au/images/8ccdb45466f9.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '62150',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 4,
      item: {
        '@type': 'Product',
        name: 'X7 Space Capsule',
        description: 'The flagship model offering maximum luxury, large panoramic views, and a spacious layout. Ultimate living experience.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/x-range-details',
        image: 'https://nexapods.com.au/images/8d92522722bb.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '66660',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 5,
      item: {
        '@type': 'Product',
        name: 'Y6 Space Capsule',
        description: 'Cabin-style living capsule with wooden exterior and panoramic balcony. Designed for a warm, nature-connected living experience with modern comforts.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/x-range-details',
        image: '/images/nx-y6-white-background-46281b.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '53130',
          availability: 'https://schema.org/InStock',
        },
      },
    },
    {
      '@type': 'ListItem',
      position: 6,
      item: {
        '@type': 'Product',
        name: 'V6 Triangle House',
        description: 'Innovative triangle-shaped double-layer living capsule with a dramatic glass facade. Maximises vertical space efficiency with two separate floors.',
        brand: { '@type': 'Brand', name: 'Nexa Pods' },
        url: 'https://nexapods.com.au/x-range-details',
        image: '/images/nx-p03-img01-4472ca.webp',
        offers: {
          '@type': 'Offer',
          priceCurrency: 'AUD',
          price: '72160',
          availability: 'https://schema.org/InStock',
        },
      },
    },
  ],
};

const xRangeProductsJsonLd = itemListToProductGraph(xRangeJsonLd, {
  canonicalPath: '/x-range-details',
});

export default function XRangeDetailsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [productsDropdownOpen, setProductsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <PageSEO
        title="X Series Luxury Living Pods | Nexa Pods Australia"
        description="Explore Nexa X Series luxury living pods in Melbourne for glamping, guest stays, backyard retreats and premium property projects across Australia."
        canonicalPath="/x-range-details"
        keywords="X Series space capsule, luxury glamping pod Australia, prefab living capsule Melbourne, X5 pod Kew VIC"
        jsonLd={xRangeProductsJsonLd}
      />
      <div className="bg-gradient-to-b from-[#0A0A0A] via-[#0D1117] to-[#000000] text-white min-h-screen w-full overflow-x-hidden">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
        scrolled ? 'bg-black/80 backdrop-blur-xl border-b border-cyan-500/20' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex items-center justify-between w-full">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0">
            <img 
              src="/images/b58bd9e3723b.webp" 
              alt="Space Capsule Living" 
              className="h-16 sm:h-20 lg:h-28 w-auto"
            />
          </Link>
          
          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-white cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Home</Link>
            
            <div 
              className="relative"
              onMouseEnter={() => setProductsDropdownOpen(true)}
              onMouseLeave={() => setProductsDropdownOpen(false)}
            >
              <Link 
                to="/product-range"
                className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-2"
              >
                Products
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
              </Link>
              
              {productsDropdownOpen && (
                <div className="absolute top-full left-0 pt-2 w-auto">
                  <div className="bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-[0_0_20px_rgba(0,217,255,0.3)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      className="block px-6 py-3 text-base text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      className="block px-6 py-3 text-base text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 hover:shadow-[inset_0_0_10px_rgba(0,217,255,0.2)] transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <a href="#models" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Models</a>
            <a href="#applications" className="text-base text-gray-300 hover:text-gray-200 transition-colors whitespace-nowrap cursor-pointer">Applications</a>
            <Link to="/contact" className="px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all hover:shadow-[0_0_20px_rgba(0,217,255,0.5)] whitespace-nowrap cursor-pointer">
              Contact Us
            </Link>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-cyan-500/20 w-full">
            <div className="px-4 py-6 space-y-4 w-full">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Home</Link>
              
              <div>
                <button 
                  onClick={() => setProductsDropdownOpen(!productsDropdownOpen)}
                  className="w-full flex items-center justify-between text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer"
                >
                  Products
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${productsDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                
                {productsDropdownOpen && (
                  <div className="mt-2 ml-4 space-y-2 bg-black/90 border border-cyan-500/30 rounded-lg shadow-[0_0_15px_rgba(0,217,255,0.2)] overflow-hidden">
                    <Link 
                      to="/product-range" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      All Products
                    </Link>
                    <Link 
                      to="/a-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      A Series - Workplace Solution
                    </Link>
                    <Link 
                      to="/x-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      X Series - Luxury Living
                    </Link>
                    <Link 
                      to="/m-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-300 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      M Series - Modularize
                    </Link>
                    <Link 
                      to="/z-range-details" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-all cursor-pointer whitespace-nowrap"
                    >
                      Z Series - Coming Soon
                    </Link>
                  </div>
                )}
              </div>

              <a href="#models" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Models</a>
              <a href="#applications" onClick={() => setMobileMenuOpen(false)} className="block text-base text-gray-300 hover:text-cyan-400 transition-colors py-2 cursor-pointer">Applications</a>
              <Link to="/contact" className="block w-full text-center px-6 py-2.5 bg-cyan-500 text-black text-base font-medium rounded-full hover:bg-cyan-400 transition-all whitespace-nowrap cursor-pointer">
                Contact Us
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Page Content */}
      <XRangeHero />
      <SeriesOverview />
      <ProductGrid />
      <TechnicalSpecifications />
      <DesignedForSmarterDeployment />
      <ApplicationShowcase />
      <CallToAction />
      <Footer />
    </div>
    </>
  );
}
